import { Loader2, RotateCcw, Search } from 'lucide-react';
import { type FormEvent } from 'react';
import { Explain } from '@/components/Explain';
import { cn } from '@/lib/cn';
import type { TimeZoneMode } from '@/lib/format';
import type { BusinessClientSummary } from '@/types';
import { ClientSelect } from '@/features/bet-inspection/components/ClientSelect';
import { GameSelect } from '@/features/bet-inspection/components/GameSelect';
import { fromHourInput, toHourInput } from '@/features/player-reports/lib/time';

export interface GameReportFilters {
  psid: string;
  gameUuid: string;
  combinedRoundId: string;
  roundId: string;
  playerId: string;
  betId: string;
  currency: string;
  /** UTC instants — the tz toggle re-renders them, never reinterprets them. */
  from: number;
  to: number;
}

/**
 * Same compact command-bar language as the console's other report screens.
 *
 * The combined round id gets its own row because it is the paste target: one
 * value from a ticket sets both the game and the bet, and when it is filled the
 * game/bet fields below it are redundant — so they visibly defer to it rather
 * than silently competing.
 */
export function FilterBar({
  value,
  onChange,
  onSearch,
  onReset,
  loading,
  clients,
  clientsLoading,
  clientsError,
  currencies,
  api,
  token,
  tz,
  base,
}: {
  value: GameReportFilters;
  onChange: (patch: Partial<GameReportFilters>) => void;
  onSearch: () => void;
  onReset: () => void;
  loading: boolean;
  clients: BusinessClientSummary[];
  clientsLoading: boolean;
  clientsError: boolean;
  currencies: string[];
  api: string;
  token: string | null;
  tz: TimeZoneMode;
  base: string;
}) {
  const combined = value.combinedRoundId.trim().length > 0;
  // Either a game (the report's scope) or a combined id (which carries one).
  const canSearch = Boolean(value.psid && (value.gameUuid || combined));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSearch) onSearch();
  };

  return (
    <form onSubmit={submit} className="border-b border-hairline px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <ClientSelect
          value={value.psid}
          onChange={(psid) => onChange({ psid })}
          clients={clients}
          loading={clientsLoading}
          error={clientsError}
        />
        <GameSelect
          value={value.gameUuid}
          onChange={(gameUuid) => onChange({ gameUuid })}
          api={api}
          token={token}
          playServiceId={value.psid}
        />

        <Field
          label="combined round id"
          value={value.combinedRoundId}
          onChange={(combinedRoundId) => onChange({ combinedRoundId })}
          width="w-[340px]"
          placeholder="<game_uuid>:<bet_id>"
          title="Paste a combined round id — it sets both the game and the bet"
        />

        <HourField label="from" ms={value.from} tz={tz} onChange={(from) => onChange({ from })} width="w-[195px]" />
        <HourField label="to" ms={value.to} tz={tz} onChange={(to) => onChange({ to })} width="w-[185px]" />
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <Field
          label="bet id"
          value={value.betId}
          onChange={(betId) => onChange({ betId })}
          width="w-[150px]"
          disabled={combined}
        />
        <Field
          label="round id"
          value={value.roundId}
          onChange={(roundId) => onChange({ roundId })}
          width="w-[150px]"
        />
        <Field
          label="player"
          value={value.playerId}
          onChange={(playerId) => onChange({ playerId })}
          width="w-[160px]"
        />

        <label className="relative block w-[168px]">
          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
            currency
          </span>
          <select
            value={value.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
            title={
              value.currency
                ? `Only ${value.currency} bets — totals exact, no conversion`
                : `All currencies; each bet keeps its own, totals converted to ${base}`
            }
            className={cn(
              'w-full appearance-none border border-hairline bg-surface-sunken py-1.5 pl-[64px] pr-2',
              'font-mono text-[12px] text-text hover:border-hairline-strong focus:border-accent focus:outline-none',
            )}
          >
            <option value="">All → {base}</option>
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 border border-hairline px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
        >
          <RotateCcw size={12} /> Reset
        </button>
        <button
          type="submit"
          disabled={!canSearch || loading}
          className={cn(
            'inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5',
            'font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent',
            'hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          Search
        </button>

        <span className="ml-auto hidden text-[11px] text-text-faint xl:block">
          {combined ? (
            <>
              The <Explain term="combined_round_id">combined round id</Explain> sets the game and
              bet.
            </>
          ) : (
            'Pick a project and a game, or paste a combined round id.'
          )}
        </span>
      </div>
    </form>
  );
}

function HourField({
  label,
  ms,
  tz,
  onChange,
  width,
}: {
  label: string;
  ms: number;
  tz: TimeZoneMode;
  onChange: (ms: number) => void;
  width: string;
}) {
  return (
    <label className={cn('relative block', width)}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        type="datetime-local"
        step={3600}
        value={toHourInput(ms, tz)}
        onChange={(e) => {
          const next = fromHourInput(e.target.value, tz);
          if (next !== null) onChange(next);
        }}
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text hover:border-hairline-strong focus:border-accent focus:outline-none',
          '[color-scheme:dark]',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  width,
  placeholder,
  title,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
}) {
  return (
    <label className={cn('relative block', width, disabled && 'opacity-45')} title={title}>
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 font-condensed text-[9px] uppercase tracking-wider text-text-faint">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        spellCheck={false}
        autoComplete="off"
        className={cn(
          'w-full border border-hairline bg-surface-sunken py-1.5 pr-2',
          'font-mono text-[12px] text-text placeholder:text-text-faint',
          'hover:border-hairline-strong focus:border-accent focus:outline-none',
          'disabled:cursor-not-allowed',
        )}
        style={{ paddingLeft: `${label.length * 5 + 16}px` }}
      />
    </label>
  );
}
